import { Link, useLocation } from "@tanstack/react-router";
import React from "react";
import { useTracking } from "./ClientAnalytics";
import type { LinkComponentProps } from "@tanstack/react-router";
import { useAppStore } from "@/stores/useApp";
import { AmplitudeTrackingEvent } from "@/types/amplitude-tracking.type";
import { buildSeriesParam } from "@/lib/showSlug";
import { SERIES_DETAILS_CAMPAIGN_ID } from "@/data/abTestCampaigns.data";
import { trackExperimentExposure } from "@/hooks/useAmplitudeExperiment";
import useIsABTestVariant from "@/hooks/useIsABTestVariant";

/** Where a catalog click goes, and whether it exposes the A/B test. */
export type DetailsPageMode = boolean | "always";

type SeriesLinkProps = LinkComponentProps<"a"> & {
  seriesId: string;
  resumeEpisodeId?: string;
  // Source `show.title`. Two consumers, both identity: the Amplitude
  // "series name" and the URL slug. Never rendered — it is not localized.
  title: string | undefined;
  episodeCount: number | undefined;
  /** Overrides the tracked `origin` (defaults to the current pathname). */
  origin?: string;
  /**
   * A catalog entry point, subject to the series details page A/B test
   * (SHO-758): the page in the treatment, the player in the control — and
   * the click is the exposure, in both. Off (the default), the link always
   * opens the player and exposes nobody: a resume surface (Continue
   * watching, Recently viewed), the hero's Play, the page's own links.
   * `"always"` opens the page in both variants and exposes nobody: a link
   * from the series details page itself (a cast member's series), where the
   * visitor is already past the decision the test measures.
   */
  detailsPage?: DetailsPageMode;
};

const SeriesLink: React.FC<SeriesLinkProps> = ({
  seriesId,
  resumeEpisodeId,
  title,
  episodeCount = 0,
  origin,
  detailsPage = false,
  ...props
}) => {
  const { trackAmplitudeEvent } = useTracking();
  const { pathname } = useLocation();
  const inTreatment = useIsABTestVariant(
    SERIES_DETAILS_CAMPAIGN_ID,
    "treatment",
  );

  const localNextEpisodeId = useAppStore(
    (s) => s.history[seriesId]?.nextEpisodeId,
  );
  // `progression.nextEpisode` when the caller fetched it, else this device's
  // own history. Both hold the same answer, written on the same event (an
  // episode reaching 90%), so they can't contradict each other.
  const episode = resumeEpisodeId || localNextEpisodeId || "watch";

  if (episodeCount === 0)
    return <span {...(props as React.ComponentProps<"span">)} />;

  const series = buildSeriesParam(title, seriesId);
  const trackedOrigin = origin ?? pathname;
  // Only a catalog entry (`true`) exposes; `"always"` opens the page without.
  const exposes = detailsPage === true;
  const opensPage = detailsPage === "always" || (exposes && inTreatment);

  const onClick = () => {
    if (exposes) trackExperimentExposure(SERIES_DETAILS_CAMPAIGN_ID);
    // The page reports its own `open series details`, reading the origin off
    // the history state — the same value `open series` carries here.
    if (opensPage) return;
    trackAmplitudeEvent(AmplitudeTrackingEvent.openSeries, {
      "series name": title ?? "Unknown",
      origin: trackedOrigin,
    });
  };

  if (opensPage)
    return (
      <Link
        {...props}
        onClick={onClick}
        to="/series/$series"
        params={{ series }}
        state={{ seriesDetailsOrigin: trackedOrigin }}
      />
    );

  return (
    <Link
      {...props}
      onClick={onClick}
      to={`/$series/$episode`}
      params={{ series, episode }}
    />
  );
};

export default SeriesLink;
